import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  draftProblem,
  EVIDENCE_MAX,
  EVIDENCE_MIN,
  type OssVerificationDraft,
  type OssVerificationRequest,
} from '../lib/ossVerification';
import { Field } from './Field';
import { buttonClass } from './ui/Button';

// Apply-for-OSS-verified form (arc 101 slice 07, #68). Presenter: it owns the
// draft and the client-side bounds check ONLY — the parent owns the POST, the
// in-flight flag, and the refetch. The check mirrors the route's Zod body so a
// typo never costs a round-trip; the server stays the authority.
export function OssVerifyForm(props: {
  /** The open application when re-applying — prefills the draft. */
  readonly initial: OssVerificationRequest | null;
  readonly busy: boolean;
  readonly onSubmit: (draft: OssVerificationDraft) => void;
}): JSX.Element {
  // Components run once: seed from `initial` here, then the signals own it.
  const [repo, setRepo] = createSignal(props.initial?.repo_full_name ?? '');
  const [evidence, setEvidence] = createSignal(props.initial?.evidence ?? '');
  const [problem, setProblem] = createSignal<'repo' | 'evidence' | null>(null);

  const submit = (e: Event): void => {
    e.preventDefault();
    const draft = { repo_full_name: repo(), evidence: evidence() };
    const bad = draftProblem(draft);
    setProblem(bad);
    if (bad === null) props.onSubmit(draft);
  };

  return (
    <form class="oss-verify__form" onSubmit={submit}>
      {/* One form per page, so fixed ids. The one error line below names
          which field failed; each control carries its own `aria-invalid`. */}
      <Field label={t('oss_verify.repo_label')} id="oss-verify-repo">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="text"
            placeholder={t('oss_verify.repo_placeholder')}
            autocomplete="off"
            autocapitalize="none"
            spellcheck={false}
            disabled={props.busy}
            aria-invalid={problem() === 'repo' || undefined}
            value={repo()}
            onInput={(e) => setRepo(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field
        label={t('oss_verify.evidence_label')}
        id="oss-verify-evidence"
        desc={t('oss_verify.evidence_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            rows={5}
            maxlength={EVIDENCE_MAX}
            spellcheck={false}
            disabled={props.busy}
            aria-invalid={problem() === 'evidence' || undefined}
            value={evidence()}
            onInput={(e) => setEvidence(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={problem()}>
        {(field) => (
          <p class="field__error" role="alert">
            {field() === 'repo'
              ? t('oss_verify.repo_error')
              : t('oss_verify.evidence_error', { min: EVIDENCE_MIN, max: EVIDENCE_MAX })}
          </p>
        )}
      </Show>

      <div class="oss-verify__actions">
        <button type="submit" class={buttonClass({ size: 'sm' })} disabled={props.busy}>
          {props.initial === null ? t('oss_verify.submit') : t('oss_verify.resubmit')}
        </button>
      </div>
    </form>
  );
}
